import { cn } from '@/lib/utils';
import { JackpotPreviewCard, BreachPreviewCard, ProtectPreviewCard, RubyCardType } from '@/components/ruby/RubyPreviewCards';

interface RubyMarkdownProps {
  content: string;
  className?: string;
  onCardClick?: (cardType: RubyCardType, action: string) => void;
}

const CARD_COMPONENTS: Record<RubyCardType, React.ComponentType<{ onClick?: () => void; className?: string }>> = {
  jackpot: JackpotPreviewCard,
  breach: BreachPreviewCard,
  protect: ProtectPreviewCard,
};

const CARD_ACTIONS: Record<RubyCardType, string> = {
  jackpot: '/earn',
  breach: '/signup',
  protect: 'https://protect.reklaimyou.com',
};

export function RubyMarkdown({ content, className, onCardClick }: RubyMarkdownProps) {
  // Parse markdown content into React elements
  const parseContent = (text: string) => {
    // Split by card tokens first
    const cardPattern = /\[CARD:(\w+)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = cardPattern.exec(text)) !== null) {
      // Add text before the card
      if (match.index > lastIndex) {
        const textBefore = text.slice(lastIndex, match.index);
        parts.push(...parseTextContent(textBefore, key));
        key += 100; // Ensure unique keys
      }

      // Add the card component
      const cardType = match[1] as RubyCardType;
      const CardComponent = CARD_COMPONENTS[cardType];
      if (CardComponent) {
        parts.push(
          <div key={`card-${key++}`} className="my-2">
            <CardComponent
              onClick={() => onCardClick?.(cardType, CARD_ACTIONS[cardType])}
            />
          </div>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last card
    if (lastIndex < text.length) {
      parts.push(...parseTextContent(text.slice(lastIndex), key));
    }

    return parts.filter(Boolean);
  };

  // Parse text content (without cards) into paragraphs
  const parseTextContent = (text: string, startKey: number) => {
    const paragraphs = text.split(/\n\n+/);
    
    return paragraphs.map((paragraph, pIndex) => {
      // Skip empty paragraphs
      if (!paragraph.trim()) return null;
      
      // Handle bullet points
      if (paragraph.trim().match(/^[-•*]\s/m)) {
        const items = paragraph.split(/\n/).filter(line => line.trim());
        return (
          <ul key={startKey + pIndex} className="space-y-1.5 my-2">
            {items.map((item, iIndex) => {
              const cleanItem = item.replace(/^[-•*]\s+/, '');
              return (
                <li key={iIndex} className="flex items-start gap-2">
                  <span className="text-primary mt-1.5 text-xs">•</span>
                  <span>{parseInline(cleanItem)}</span>
                </li>
              );
            })}
          </ul>
        );
      }
      
      // Handle headings (## or ###)
      if (paragraph.trim().startsWith('#')) {
        const headingMatch = paragraph.match(/^(#{1,3})\s+(.+)$/);
        if (headingMatch) {
          const level = headingMatch[1].length;
          const headingText = headingMatch[2];
          return (
            <p key={startKey + pIndex} className={cn(
              "font-semibold text-foreground mt-3 mb-1",
              level === 1 && "text-base",
              level >= 2 && "text-sm"
            )}>
              {parseInline(headingText)}
            </p>
          );
        }
      }
      
      // Regular paragraph - handle single line breaks within
      const lines = paragraph.split(/\n/);
      return (
        <p key={startKey + pIndex} className="mb-2 last:mb-0">
          {lines.map((line, lIndex) => (
            <span key={lIndex}>
              {parseInline(line)}
              {lIndex < lines.length - 1 && <br />}
            </span>
          ))}
        </p>
      );
    });
  };

  // Parse inline formatting (bold, italic)
  const parseInline = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Match bold (**text** or __text__)
      const boldMatch = remaining.match(/^(.*?)(\*\*|__)(.+?)\2(.*)$/s);
      
      if (boldMatch) {
        // Add text before bold
        if (boldMatch[1]) {
          parts.push(<span key={key++}>{boldMatch[1]}</span>);
        }
        // Add bold text
        parts.push(
          <strong key={key++} className="font-semibold text-foreground">
            {boldMatch[3]}
          </strong>
        );
        remaining = boldMatch[4];
      } else {
        // No more formatting, add remaining text
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>;
  };

  return (
    <div className={cn("leading-relaxed", className)}>
      {parseContent(content)}
    </div>
  );
}